import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import Loading from 'views/App/Portal/Loading';
import useDraftPolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPolicy';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import useManagePolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePolicy';
import draftManualHelper from '../../helpers/draftManualHelper';
import PolicyContentTree from './_components/PolicyContentTree';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';

const GenerateChangeset: React.FC = () => {
  const { id: manualId, content: contentParam } = useParams<{ id: string; content: string }>();
  const navigate = useNavigate();
  const { recreateChangeSets } = useManagePolicy();
  const [selectedContentIds, setSelectedContentIds] = useState<string[]>([]);
  const [includeGuideSheet, setIncludeGuideSheet] = useState(true);
  const [includeReleaseNotes, setIncludeReleaseNotes] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract policyId from content param (format: "policy:policyId")
  const policyId = contentParam?.startsWith('policy:') ? contentParam.replace('policy:', '') : undefined;

  const manualQuery = useDraftManual(manualId);
  const policyQuery = useDraftPolicy(manualId, policyId, true, {}, undefined);

  const policyData = policyQuery.data;
  const manual = manualQuery.data;

  // Get all content IDs for helper functions
  const getAllSectionIds = useCallback((): string[] => {
    if (!policyData?.sections) return [];
    return policyData.sections.map((section) => section.id);
  }, [policyData]);

  const getAllSubsectionIds = useCallback(
    (sectionId: string): string[] => {
      if (!policyData?.sections) return [];
      const section = policyData.sections.find((s) => s.id === sectionId);
      return section?.subSections?.map((sub) => sub.id) || [];
    },
    [policyData]
  );

  const getAllContentIds = useCallback((): string[] => {
    const sectionIds = getAllSectionIds();
    const subsectionIds = policyData?.sections?.flatMap((section) => getAllSubsectionIds(section.id)) || [];
    return [policyData?.id || '', ...sectionIds, ...subsectionIds].filter(Boolean);
  }, [policyData, getAllSectionIds, getAllSubsectionIds]);

  // Check if all subsections of a section are selected
  const areAllSubsectionsSelected = useCallback(
    (sectionId: string): boolean => {
      const subsectionIds = getAllSubsectionIds(sectionId);
      if (subsectionIds.length === 0) {
        // Section has no subsections - return true if section itself is selected
        return selectedContentIds.includes(sectionId);
      }
      return subsectionIds.every((id) => selectedContentIds.includes(id));
    },
    [selectedContentIds, getAllSubsectionIds]
  );

  // Check if some (but not all) subsections are selected
  const areSomeSubsectionsSelected = useCallback(
    (sectionId: string): boolean => {
      const subsectionIds = getAllSubsectionIds(sectionId);
      if (subsectionIds.length === 0) return false;
      const selectedCount = subsectionIds.filter((id) => selectedContentIds.includes(id)).length;
      return selectedCount > 0 && selectedCount < subsectionIds.length;
    },
    [selectedContentIds, getAllSubsectionIds]
  );

  // Check if all sections and subsections are selected
  const areAllContentSelected = useCallback((): boolean => {
    const allIds = getAllContentIds();
    return allIds.length > 0 && allIds.every((id) => selectedContentIds.includes(id));
  }, [selectedContentIds, getAllContentIds]);

  // Check if some (but not all) content is selected
  const areSomeContentSelected = useCallback((): boolean => {
    const allIds = getAllContentIds();
    const selectedCount = allIds.filter((id) => selectedContentIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < allIds.length;
  }, [selectedContentIds, getAllContentIds]);

  // Handle policy checkbox change
  const handlePolicyToggle = useCallback(() => {
    const allIds = getAllContentIds();
    if (areAllContentSelected()) {
      // Deselect all
      setSelectedContentIds([]);
    } else {
      // Select all
      setSelectedContentIds(allIds);
    }
  }, [areAllContentSelected, getAllContentIds]);

  // Handle section checkbox change
  const handleSectionToggle = useCallback(
    (sectionId: string) => {
      const subsectionIds = getAllSubsectionIds(sectionId);
      const allSectionIds = subsectionIds.length > 0 ? [sectionId, ...subsectionIds] : [sectionId];

      if (
        selectedContentIds.includes(sectionId) &&
        (subsectionIds.length === 0 || areAllSubsectionsSelected(sectionId))
      ) {
        // Deselect section and all subsections
        setSelectedContentIds((prev) => prev.filter((id) => !allSectionIds.includes(id)));
      } else {
        // Select section and all subsections
        setSelectedContentIds((prev) => {
          const newIds = [...prev];
          allSectionIds.forEach((id) => {
            if (!newIds.includes(id)) {
              newIds.push(id);
            }
          });
          return newIds;
        });
      }
    },
    [selectedContentIds, areAllSubsectionsSelected, getAllSubsectionIds]
  );

  // Handle subsection checkbox change
  const handleSubsectionToggle = useCallback(
    (subsectionId: string, sectionId: string) => {
      setSelectedContentIds((prev) => {
        if (prev.includes(subsectionId)) {
          // Deselect subsection and section if it was selected
          const newIds = prev.filter((id) => id !== subsectionId);
          return newIds.filter((id) => id !== sectionId);
        } else {
          // Select subsection
          const newIds = [...prev, subsectionId];
          // If all subsections are now selected, also select the section
          const subsectionIds = getAllSubsectionIds(sectionId);
          if (subsectionIds.every((id) => newIds.includes(id))) {
            newIds.push(sectionId);
          }
          return newIds;
        }
      });
    },
    [getAllSubsectionIds]
  );

  // Get policy number
  const policyNumber = policyData && manual ? draftManualHelper.getNumberManualNode(policyData, manual) : '';

  // Helper to get formatted policy title as string
  // Uses the same logic as formatManualNodeTitle but returns a plain string
  const getFormattedPolicyTitle = useCallback((): string => {
    if (!policyData || !manual) return '';
    const cleanTitle =
      policyData.title
        ?.replace(/&amp;/g, '&')
        ?.replace(/&lt;/g, '<')
        ?.replace(/&gt;/g, '>')
        ?.replace(/&quot;/g, '"')
        ?.replace(/&#39;/g, "'")
        ?.replace(/<[^>]*>/g, '') ?? '';
    const num = draftManualHelper.getNumberManualNode(policyData, manual);
    if (num && num.length > 0) {
      return `${num}: ${cleanTitle}`;
    }
    return cleanTitle;
  }, [policyData, manual]);

  // Helper to get formatted title for a content item
  const getFormattedContentTitle = useCallback(
    (contentId: string): string => {
      if (!policyData || !manual) return '';

      // Check if it's the policy itself
      if (contentId === policyData.id) {
        return getFormattedPolicyTitle();
      }

      // Check if it's a section
      const section = policyData.sections?.find((s) => s.id === contentId);
      if (section) {
        return draftManualHelper.formatPolicyNodeTitle(section, policyNumber, policyData);
      }

      // Check if it's a subsection
      for (const sectionItem of policyData.sections || []) {
        const subsection = sectionItem.subSections?.find((ss) => ss.id === contentId);
        if (subsection) {
          return draftManualHelper.formatPolicyNodeTitle(subsection, policyNumber, policyData);
        }
      }

      return '';
    },
    [policyData, manual, policyNumber, getFormattedPolicyTitle]
  );

  const canGenerate =
    selectedContentIds.length > 0 || includeGuideSheet || includeReleaseNotes;

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!canGenerate) {
      enqueueSnackbar(t('draftManuals.generateChangeset.noSelection'), { variant: 'warning' });
      return;
    }

    if (!policyData || !manual || !manualId || !policyId) {
      enqueueSnackbar(t('draftManuals.generateChangeset.error'), { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    recreateChangeSets(
      manualId,
      policyId,
      {
        content: selectedContentIds.map((id) => ({
          id,
          title: getFormattedContentTitle(id),
        })),
        contentTitle: getFormattedPolicyTitle(),
        includeGuideSheet,
        includeReleaseNotes,
      },
      () => {
        setIsSubmitting(false);
        enqueueSnackbar(t('draftManuals.generateChangeset.success'), { variant: 'success' });
        navigate(`/admin/policy/draft-manuals/${manualId}/policy:${policyId}`);
      },
      (error: ServerErrorResponse) => {
        setIsSubmitting(false);
        errorHelper.enqueueErrors(error);
      }
    );
  }, [
    selectedContentIds,
    policyData,
    manual,
    manualId,
    policyId,
    policyNumber,
    getFormattedPolicyTitle,
    getFormattedContentTitle,
    recreateChangeSets,
    navigate,
    includeGuideSheet,
    includeReleaseNotes,
    canGenerate,
  ]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (policyId) {
      navigate(`/admin/policy/draft-manuals/${manualId}/policy:${policyId}`);
    } else {
      navigate(`/admin/policy/draft-manuals/${manualId}`);
    }
  }, [navigate, manualId, policyId]);

  if (policyQuery.isLoading || manualQuery.isLoading) {
    return <Loading />;
  }

  if (!policyData || !manual) {
    return (
      <TakeoverLayout
        pageTitle={t('draftManuals.generateChangeset.title')}
        backUrl={`/admin/policy/draft-manuals/${manualId}`}
      >
        <Box sx={{ p: 4 }}>
          <Typography>{t('draftManuals.generateChangeset.error')}</Typography>
        </Box>
      </TakeoverLayout>
    );
  }

  const policyCheckboxState = areAllContentSelected()
    ? 'checked'
    : areSomeContentSelected()
      ? 'indeterminate'
      : 'unchecked';

  return (
    <TakeoverLayout
      pageTitle={t('draftManuals.generateChangeset.title')}
      backUrl={`/admin/policy/draft-manuals/${manualId}/policy:${policyId}`}
      contentHeaderRight={
        <Stack
          direction='row'
          spacing={2}
        >
          <Button
            variant='outlined'
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {t('draftManuals.generateChangeset.cancel')}
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={isSubmitting || !canGenerate}
            loading={isSubmitting}
            loadingPosition='end'
          >
            {t('draftManuals.generateChangeset.generate')}
          </Button>
        </Stack>
      }
    >
      <Box sx={{ p: 4 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant='h6'>
              {t('draftManuals.generateChangeset.policyNumber', '', { number: policyNumber })}
            </Typography>
            <Typography
              variant='body1'
              sx={{ mt: 1 }}
            >
              {policyData.title}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant='body2'
              sx={{ mb: 2, color: 'text.secondary' }}
            >
              {t('draftManuals.generateChangeset.selectContent')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant='body2'>
                {t('draftManuals.generateChangeset.itemsSelected', '', {
                  count: selectedContentIds.length.toString(),
                })}
              </Typography>
              <Stack direction='row' spacing={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeGuideSheet}
                      onChange={(e) => setIncludeGuideSheet(e.target.checked)}
                      size='small'
                    />
                  }
                  label={t('draftManuals.generateChangeset.includeGuideSheets')}
                  labelPlacement='start'
                  sx={{ '& .MuiFormControlLabel-label': { mr: 2 } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeReleaseNotes}
                      onChange={(e) => setIncludeReleaseNotes(e.target.checked)}
                      size='small'
                    />
                  }
                  label={t('draftManuals.generateChangeset.includeReleaseNotes')}
                  labelPlacement='start'
                  sx={{ '& .MuiFormControlLabel-label': { mr: 2 } }}
                />
              </Stack>
            </Box>
            <PolicyContentTree
              policyData={policyData}
              manual={manual}
              selectedContentIds={selectedContentIds}
              policyCheckboxState={policyCheckboxState}
              onPolicyToggle={handlePolicyToggle}
              onSectionToggle={handleSectionToggle}
              onSubsectionToggle={handleSubsectionToggle}
              areAllSubsectionsSelected={areAllSubsectionsSelected}
              areSomeSubsectionsSelected={areSomeSubsectionsSelected}
            />
          </Box>
        </Stack>
      </Box>
    </TakeoverLayout>
  );
};

export default GenerateChangeset;
